import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase, safeLocalSignOut } from '@/lib/supabase';
import { isAuthorizedAdmin } from '@/lib/admin-access';

interface AuthGuardProps {
  children: React.ReactNode;
}

export default function AuthGuard({ children }: AuthGuardProps) {
  const navigate = useNavigate();
  const [isChecking, setIsChecking] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authError, setAuthError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    supabase.auth.getSession().then(({ data: { session } }) => {
      if (cancelled) return;
      if (session && isAuthorizedAdmin(session.user?.email)) {
        setIsAuthenticated(true);
      } else {
        // Not signed in, or signed in with a non-admin account — clear and redirect.
        if (session) {
          safeLocalSignOut();
        }
        // Preserve cTrader OAuth callback code across login redirect
        const params = new URLSearchParams(window.location.search);
        const code = params.get('code');
        if (code) {
          localStorage.setItem('ctrader_pending_oauth_code', code);
        }
        navigate('/login');
      }
      setIsChecking(false);
    }).catch((err) => {
      // Refresh token expired or invalid — wipe local session (no server call)
      console.warn('[AuthGuard] Session check failed, signing out locally:', err?.message || err);
      safeLocalSignOut().finally(() => {
        if (cancelled) return;
        setAuthError('Session expired. Please sign in again.');
        setIsChecking(false);
        setIsAuthenticated(false);
        navigate('/login');
      });
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      if (cancelled) return;
      if (!session || !isAuthorizedAdmin(session.user?.email)) {
        setIsAuthenticated(false);
        if (session) safeLocalSignOut();
        navigate('/login');
      }
    });

    return () => {
      cancelled = true;
      subscription.unsubscribe();
    };
  }, [navigate]);

  if (isChecking) {
    return (
      <div className="flex items-center justify-center h-screen" style={{ backgroundColor: '#ffffff' }}>
        <div className="flex flex-col items-center gap-4">
          <div className="w-10 h-10 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin"></div>
          <p className="text-foreground-800 text-sm font-medium">Checking session...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    if (authError) {
      return (
        <div className="flex items-center justify-center h-screen" style={{ backgroundColor: '#ffffff' }}>
          <div className="flex flex-col items-center gap-4 text-center px-6">
            <i className="ri-error-warning-line text-4xl text-amber-400 w-10 h-10 flex items-center justify-center"></i>
            <div>
              <p className="text-foreground-800 font-semibold text-sm mb-1">Session Expired</p>
              <p className="text-foreground-500 text-xs">{authError}</p>
            </div>
            <button
              onClick={() => navigate('/login')}
              className="px-4 py-2 rounded-md bg-primary-500 text-background-50 text-sm cursor-pointer hover:bg-primary-600 transition-colors whitespace-nowrap"
            >
              Sign In Again
            </button>
          </div>
        </div>
      );
    }
    return null;
  }

  return <>{children}</>;
}